import { DOCUMENT } from '@angular/common';
import {
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import { AppendComponentService } from '../../services/append-component.service';
import { Position } from '../types';
import { TooltipComponent } from './tooltip.component';

@Directive({
  selector: '[tooltip]'
})
export class TooltipDirective implements OnInit, OnDestroy, OnChanges {
  @Input() tooltip: TemplateRef<any> | string;

  @Input() tooltipData: any;

  @Input() position: Position = Position.top;

  @Input() tooltipWidth = '';

  @Input() tooltipMaxWidth = '';

  @Input() delay = 500;

  @Input() overflowOnly = false;

  @Input() disableTooltip?: boolean = false;

  @Output() openChange = new EventEmitter<boolean>();
  private $open = false;

  @Input() set open(o: boolean) {
    this.$open = o;
    this.$open && this.show();
    !this.$open && this.hide();
  }

  get open(): boolean {
    return this.$open;
  }

  private contentRef: ComponentRef<TooltipComponent>;
  private showTimeout: number;
  private ignoreScrollEvent = false;
  private window: Window;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private appendComponent: AppendComponentService,
    private element: ElementRef
  ) {
    this.window = (this.document as Document).defaultView;
  }

  ngOnInit(): void {
    this.element.nativeElement.addEventListener('mouseenter', this.show);
    this.element.nativeElement.addEventListener('mouseleave', this.hide);
    this.element.nativeElement.addEventListener('mousedown', this.hide);
  }

  ngOnDestroy(): void {
    this.element.nativeElement.removeEventListener('mouseenter', this.show);
    this.element.nativeElement.removeEventListener('mouseleave', this.hide);
    this.element.nativeElement.removeEventListener('mousedown', this.hide);
    this.document.removeEventListener('scroll', this.hide, true);

    this.hide();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && ((changes.disableTooltip && this.disableTooltip) || (changes.tooltip && !this.tooltip))) {
      this.hide();
    }

    if (changes && changes.tooltipData && this.contentRef) {
      const tooltip: TooltipComponent = this.contentRef.instance;
      tooltip.tooltipData = this.tooltipData;
    }
  }

  private show = () => {
    const target = this.element.nativeElement;
    if (this.disableTooltip) return;
    if (!this.tooltip) return;
    if (target instanceof HTMLInputElement && this.overflowOnly) {
      if (this.placeholderOverFlow(target)) return;
    } else if (
      this.overflowOnly &&
      target.clientWidth === target.scrollWidth &&
      target.clientHeight === target.scrollHeight
    )
      return;

    if (this.showTimeout) return;
    this.showTimeout = this.window.setTimeout(() => {
      this.showTimeout = undefined;
      if (this.disableTooltip) return;
      if (!this.tooltip) return;
      if (!this.document.contains(this.element.nativeElement)) return;

      this.$open = true;

      this.addTooltipComponent();

      this.openChange.emit(this.$open);
    }, this.delay);
  };

  private placeholderOverFlow(target) {
    const input = target as HTMLInputElement;
    const tempValue = target.value;
    input.value = input.placeholder;
    let visiblePlaceholder = true;
    tempValue === '' ? (visiblePlaceholder = input.scrollWidth === input.clientWidth) : (visiblePlaceholder = true);
    input.value = tempValue;
    return visiblePlaceholder;
  }

  private addTooltipComponent() {
    // remove any previous added content
    this.removeTooltipComponent();

    // add Tooltip Component to the DOM
    this.contentRef = this.appendComponent.appendComponent(TooltipComponent);
    const tooltip: TooltipComponent = this.contentRef.instance;
    // add component inputs
    tooltip.target = this.element.nativeElement;
    tooltip.position = this.position;
    tooltip.width = this.tooltipWidth;
    tooltip.maxWidth = this.tooltipMaxWidth;
    tooltip.content = this.tooltip;
    tooltip.tooltipData = this.tooltipData;

    this.ignoreScrollEvent = true;
    this.document.addEventListener('scroll', this.hide, true);
    this.window.setTimeout(() => {
      this.ignoreScrollEvent = false;
    }, 100);
  }

  private hide = ($event: any = {}) => {
    if ($event.type === 'scroll' && this.ignoreScrollEvent) {
      return;
    }
    this.window.clearTimeout(this.showTimeout);
    this.showTimeout = undefined;
    this.$open = false;
    this.removeTooltipComponent();
    this.openChange.emit(this.$open);
  };

  private removeTooltipComponent() {
    // remove Tooltip Component from the DOM
    this.contentRef && this.appendComponent.removeComponent(this.contentRef);
    this.contentRef = undefined;

    this.document.removeEventListener('scroll', this.hide, true);
  }
}
